/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { Button } from '@mui/material';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Path, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import * as constants from '../common/constants';
import { modelAccessInfo } from '../common/queryFunctions';
import { useSingleTab } from '../common/hooks/useSingleTab';
import { keepAlive, sessionStatus, updateUserStorage, setAbsoluteTimeout } from '../common/UtilityFunctions';
import DefaultModal from '../components/modals/DefaultModal';
import Styles from '../css/modals/AuthContextModal.module.css';
import { removeUserSession, setLoggedOut } from '../storage/userStorage';
import axiosCall from '../services/axios';

type authValue = Partial<WebUser> & {
  company?: string;
  externalUser?: boolean;
  firstName?: string;
  fullAccess?: boolean;
  fullAccessSupervisor?: boolean;
  fullAccessSuperUser?: boolean;
  helocEditAccess?: boolean;
  fullAccessGNMA?: boolean;
  hasProctorReportAccess?: boolean;
  lastName?: string;
  servicerUser?: boolean;
  superUser?: boolean;
  token?: string;
  userId?: string;
  validId?: boolean;
  validPassword?: boolean;
  validServicerKey?: boolean;
  updateUser?: (data: Partial<authValue>) => void;
  updateBorrowerUser?: (webuser: WebUser) => void;
  clearUser?: () => void;
  logout?: (beforeLogout?: () => Promise<void>) => Promise<void>;
};

const AuthContext = createContext<authValue | null>(null);

const useAuthContext = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('authContext must be inside a AuthProvider');
  }
  return authContext;
};

type authProviderProps = {
  children: ReactNode;
};

const AuthProvider = ({ children }: authProviderProps): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams(); // To retrieve query parameters
  const key = searchParams.get('key') || '';
  const [user, setUser] = useState<Partial<authValue>>({});
  const [open, setOpen] = useState<boolean>(false);

  const initializedUserRef = useRef(false);
  const [expires, setExpires] = useState(0);
  const token = useMemo(() => (user && user.token ? user.token : ''), [user]);
  const [countdown, setCountdown] = useState(constants.timeoutWarningSeconds);
  const interactModalRef = useRef(false);
  const { getIsSecondTab } = useSingleTab();

  const clearUser = useCallback(() => {
    setUser({});
    localStorage.removeItem('key');
  }, []);

  const logout = useCallback(
    async (beforeLogout?: () => Promise<void>) => {
      const isServicer = user.servicerUser;

      // Preserve the authenticated session until the audit request completes
      if (beforeLogout) {
        try {
          await beforeLogout();
        } catch (error) {
          console.error('Error capturing logout audit:', error);
        }
      }

      setOpen(false);
      clearUser();
      removeUserSession();
      setLoggedOut(true);
      setExpires(0);
      interactModalRef.current = true;
      initializedUserRef.current = false;
      if (isServicer) {
        navigate('/login-servicer');
      } else {
        navigate('/login');
      }
    },
    [user, clearUser, navigate],
  );

  const keepTokenAlive = useCallback(async () => {
    setOpen(false);
    const expires = await keepAlive(token);
    if (expires < 1) return logout();
    interactModalRef.current = true;
    setExpires((prev) => (prev === expires ? expires - 1 : expires));
  }, [token, logout]);

  const updateUser = useCallback(
    async (data) => {
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        console.error(
          `updateUser called with data of type ${
            !data ? 'null' : Array.isArray(data) ? 'array' : typeof data
          } instead of object`,
        );
        return;
      }
      setUser((prev) => {
        if (!prev.token) {
          if (getIsSecondTab()) {
            alert('Only one instance can be open at a time. Please close this tab.');
            return {};
          }
        }
        const next = { ...prev, ...data };
        return next;
      });
    },
    [getIsSecondTab],
  );

  const initializeUser = useCallback(async () => {
    // update pathsNoLoginRequired if new pages are added that do not require being logged in
    // Do not use react-router-dom useLocation.pathname here as we do not want to retrigger the function
    if (constants.pathsNoLoginRequired.includes('/' + window.location.pathname.split('/').pop())) return;
    if (initializedUserRef.current) return;
    initializedUserRef.current = true;
    const isServicer = location.state?.servicer || user.servicerUser;
    const params = await updateUserStorage(key || localStorage.getItem('key'), isServicer); //UserId is in this params object, somewhere
    if (!params || typeof params !== 'object') {
      // borrower with no key in URL
      if (window.location.pathname.split('/').pop() === 'login-servicer') return;
      if (!key) return navigate('/login');
      // servicer with bad key in URL
      console.log('Servicer login failed, invalid key.');
      setUser({});
      return navigate('/login-servicer');
    }

    const modelAccess = await modelAccessInfo(
      `${process.env['REACT_APP_TEAM_MODEL']}`,
      params.servicerUser,
      params.token,
    );
    const modelAccessSupervisor = await modelAccessInfo(
      `${process.env['REACT_APP_SUPERVISOR_MODEL']}`,
      params.servicerUser,
      params.token,
    );
    const modelAccessSuperuser = await modelAccessInfo(
      `${process.env['REACT_APP_OVERRIDE_MODEL']}`,
      params.servicerUser,
      params.token,
    );
    const modelAccessHeloc = await modelAccessInfo(
      `${process.env['REACT_APP_HELOC_EDIT_MODEL']}`,
      params.servicerUser,
      params.token,
    );
    const modelAccessGNMA = await modelAccessInfo(
      `${process.env['REACT_APP_GNMA_MODEL']}`,
      params.servicerUser,
      params.token,
    );

    let hasProctorReportAccess = false;
    if (params.servicerUser && params.userId) {
      try {
        const config = {};

        const headers = {
          'Session-Token': params.token,
        };

        const response = await axiosCall(
          {
            url: `live/master/user/${params.userId}/preferences`,
            method: 'post',
            data: {
              name: 'ALLOW_GEN_INS_DISBURSEMENT',
            },
          },
          config,
          headers,
        );

        hasProctorReportAccess = response?.value === '1';
      } catch (error) {
        console.error('Error retrieving ALLOW_GEN_INS_DISBURSEMENT preference:', error);
      }
    }

    const expires = await sessionStatus(params.token);
    if (expires < 1) {
      setUser({});
      localStorage.removeItem('key');
      if (params.servicerUser) return navigate('/login-servicer');
      return navigate('/login');
    }
    setExpires((prev) => (prev === expires ? expires - 1 : expires));
    if (params.loanNumber)
      navigate(null as unknown as Path, { replace: true, state: { loanNumber: params.loanNumber } });
    delete params.expires;
    if (
      params.servicerUser &&
      !(modelAccess?.readAccess || modelAccessSupervisor?.readAccess || modelAccessSuperuser?.readAccess)
    )
      return navigate('/login-servicer');
    await updateUser({
      ...params,
      validServicerKey: params.servicerUser || undefined,
      fullAccess: modelAccess?.fullAccess,
      fullAccessSupervisor: modelAccessSupervisor?.fullAccess,
      fullAccessSuperUser: modelAccessSuperuser?.fullAccess,
      helocEditAccess: modelAccessHeloc?.fullAccess,
      fullAccessGNMA: modelAccessGNMA?.fullAccess,
      hasProctorReportAccess,
    });
    initializedUserRef.current = true;
  }, [key, navigate, updateUser]);

  const updateBorrowerUser = useCallback((webuser: WebUser) => {
    if (!webuser.email || !webuser.webUserId || webuser.email === '' || webuser.webUserId === '') {
      console.error(
        'Cannot accept empty email address or webUserId for borrower users. Email: ' +
          webuser.email +
          ', webUserId: ' +
          webuser.webUserId,
      );
      return;
    }
    setUser((prev) => ({ ...prev, ...webuser, servicerUser: false }));
  }, []);

  /**
   * Please note that best practice is to avoid the useEffect by defining initializeUser
   * as a helper function outside of the component with const [user, setUser] = useState(initializeUser);
   * This does not work for async functions though so we need to go through useEffect.
   */
  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  useEffect(() => {
    // Do not use react-router-dom useLocation.pathname here as we do not want to retrigger the useEffect
    if (!expires || constants.pathsNoLoginRequired.includes('/' + window.location.pathname.split('/').pop())) return;
    const delay = Math.min(expires * 1000 - 5000, 2147483647);
    setCountdown(constants.timeoutWarningSeconds);

    // have minimum of 1000 (1 second) to avoid negative delay.
    // this lead to status calling sessionStatus() many, many times (every 4ms)
    const statusSeconds = constants.timeoutWarningSeconds + 10;
    const statusTime = Math.max(delay - statusSeconds * 1000, 1000);
    const warningTime = Math.max(delay - constants.timeoutWarningSeconds * 1000, 1000);
    const startTime = Date.now();

    const clearStatus = setAbsoluteTimeout(async () => {
      const expires = await sessionStatus(token);
      if (expires < 1) return logout();
      if (expires > statusSeconds + 5) return setExpires((prev) => (prev === expires ? expires - 1 : expires));
      // do nothing and prompt the user
    }, statusTime);
    let countdownTimer;
    const clearWarning = setAbsoluteTimeout(() => {
      setOpen(true);
      interactModalRef.current = false;
      let timer: number = Math.ceil((startTime + delay - Date.now()) / 1000);
      setCountdown(timer);
      countdownTimer = setInterval(() => {
        // clearInterval if user clicks Yes/No modal or timer runs out. logout if timer runs out.
        if (interactModalRef.current) {
          interactModalRef.current = false;
          return clearInterval(countdownTimer);
        } else if (timer <= 0) {
          logout();
          return clearInterval(countdownTimer);
        }
        timer = Math.ceil((startTime + delay - Date.now()) / 1000);
        setCountdown(timer);
      }, 1000);
    }, warningTime);
    return () => {
      clearStatus();
      clearWarning();
      clearInterval(countdownTimer);
    };
  }, [expires, logout, token, navigate]);

  return (
    <AuthContext.Provider value={{ ...user, updateUser, updateBorrowerUser, clearUser, logout }}>
      {children}
      <DefaultModal open={Boolean(open && user.token)} className={Styles.css}>
        <p>
          Your session will expire in
          <br />
          {countdown} {countdown === 1 ? 'second' : 'seconds'}.<br />
          Would you like to extend it?
        </p>
        <Button type='button' onClick={keepTokenAlive}>
          Yes
        </Button>
        <Button type='button' onClick={() => logout()}>
          No
        </Button>
      </DefaultModal>
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuthContext };
